<script setup>
import { computed, markRaw, onBeforeMount, reactive } from 'vue';
import { RouterLink, RouterView, useRouter } from 'vue-router';

import { useProfileResource, collectionV2 } from '@/composables/resource';
import { PostType } from '@/enums';
import { useStore } from '@/store';
import { shouldShowItem, abbreviateNumber } from '@/utils';

import Avatar from '@/components/Avatar.vue';
import IconMessageTextOutline from '@/components/Icons/MessageTextOutline.vue';
import IconLocationRadius from '@/components/Icons/LocationRadius.vue';
import IconSchool from '@/components/Icons/School.vue';
import IconUser from '@/components/Icons/User.vue';
import IconUsersGroupOutline from '@/components/Icons/UsersGroupOutline.vue';
import IconUserMultipleAddOutline from '@/components/Icons/UserMultipleAddOutline.vue';
import IconUserAdd from '@/components/Icons/UserAdd.vue';
import IconUserCheck from '@/components/Icons/UserCheck.vue';
import IconUserRemove from '@/components/Icons/UserRemove.vue';
import IconViewStream from '@/components/Icons/ViewStream.vue';
import IconStar from '@/components/Icons/Star.vue';
import LineClamp from '@/components/LineClamp.vue';
import PostList from '@/components/Post/PostList.vue';
import SettingsOutline from '@/components/Icons/SettingsOutline.vue';
import Skeleton from '@/components/Renderer/Skeleton.vue';
import TabsList from '@/components/Tabs/TabsList.vue';
import TabsRoot from '@/components/Tabs/TabsRoot.vue';
import TabsTrigger from '@/components/Tabs/TabsTrigger.vue';
import TabsContent from '@/components/Tabs/TabsContent.vue';
import UserStar from '@/components/User/UserStar.vue';

const props = defineProps({
    pageData: {
        type: Object,
        required: false,
    },
    username: {
        type: String,
        required: true,
    }
});

const state = reactive({
    activeTab: 'posts',
});

const store = useStore();
const router = useRouter();
const is_current_user = store.state.user && store.state.user.profile.username === props.username;

const resource = useProfileResource(props.username);
const resourceRelations = reactive({
    posts: collectionV2({
        url: '/api/posts',
    }),
    questions: collectionV2({
        url: '/api/posts',
    }),
});

if (props.pageData?.data) {
    resource.fill(props.pageData);
} else if (is_current_user) {
    resource.fill({ data: store.state.user});
}

const setRelations = () => {
    resourceRelations.posts.setParams({
        filters: {
            author: resource.data.id,
            type: PostType.POST,
        },
    });

    resourceRelations.questions.setParams({
        filters: {
            author: resource.data.id,
            type: PostType.QUESTION,
        },
    });

    if (!resourceRelations.posts.fetched) {
        resourceRelations.posts.fetch();
    }

    if (!resourceRelations.questions.fetched) {
        resourceRelations.questions.fetch();
    }
}


const startChat = (userId) => {
    router.push({
        name: "conversation.create",
        query: {
            id: userId,
        },
    });
};

const menuItems = computed(() => {
    return [
        {
            label: 'Connections',
            icon: markRaw(IconUsersGroupOutline),
            to: { name: 'connections.index' },
            show: store.state.user && is_current_user,
        },
        {
            label: 'Blocked Users',
            icon: markRaw(IconUserRemove),
            to: { name: 'blocked-users.index' },
            show: store.state.user && is_current_user,
        },
        {
            label: 'Referrals',
            icon: markRaw(IconUserMultipleAddOutline),
            to: '/referrals',
            show: store.state.user && is_current_user,
        },
        {
            label: 'Reputation Score',
            icon: markRaw(IconStar),
            to: { name: 'profiles.show.reputation' },
            show: store.state.user && is_current_user,
        },
        {
            label: resource.data?.blocked ? 'Unblock' : 'Block',
            icon: 'icon-block',
            show: store.state.user && !is_current_user,
            onSelect: () => {
                store.blockUser({
                    unblock: resource.data.blocked,
                    user: resource.data
                }).then((result) => {
                    if (!result.success) {
                        return;
                    }

                    resource.data.blocked = !resource.data.blocked;
                });
            },
        },
    ];
});

const showMenu = computed(() => {
    return menuItems.value.filter(item => shouldShowItem(item)).length > 0;
});

const connection = computed(() => {
    if (!resource.data) {
        return null;
    }

    return resource.data.connection_sent_to_me || resource.data.connection_received_from_me;
});

onBeforeMount(() => {
    if (!resource.filled()) {
        resource.fetch({
            onsuccess: () => setRelations(),
        });
    } else {
        setRelations();
    }
});
</script>

<template>
    <layout-main>
        <head-title v-if="resource.filled()" :value="`${resource.data.name} (@${resource.data.profile.username})`" />
        <layout-content>
            <content-header>
                <top-nav>
                    <top-nav-back />
                    <top-nav-title>
                        Profile
                    </top-nav-title>
                    <div class="grow"></div>

                    <RouterLink v-if="is_current_user" class="ml-auto" :to="{ name: 'settings.edit-profile' }">
                        <SettingsOutline class="size-6" />
                        <span class="sr-only">Edit Profile</span>
                    </RouterLink>

                    <top-nav-menu v-if="showMenu" :items="menuItems" />
                </top-nav>
            </content-header>

            <content-body>
                <div v-if="!resource.filled() && resource.fetching()">
                    <Skeleton :count="1" ignore-height />
                </div>

                <template v-if="resource.filled()">
                    <div class="bg-white text-white-foreground p-4">
                        <div class="flex flex-row gap-2 items-start">
                            <div class="shrink-0 relative">
                                <Avatar as-link size="xl" :src="resource.data.profile.avatar?.url" :image="resource.data.profile.avatar" :fallback-from="resource.data.name" />
                            </div>
                            <div class="flex-grow flex flex-col justify-center">
                                <app-account-badge :user="resource.data">
                                    <h1 class="text-xl font-bold leading-none">{{ resource.data.name }}</h1>
                                </app-account-badge>
                                <p class="leading-none text-sm opacity-70 mt-1">@{{ resource.data.profile.username }}</p>
                                <p class="leading-none text-sm opacity-70 mt-2" v-if="resource.data.account_type == 'official'">Offical Corperland Account</p>
                                <button type="button"
                                    class="bg-gray dark:bg-white-foreground/10 px-1.5 py-0.5 flex gap-1 items-center rounded-md w-fit mt-2"
                                    @click="router.push({ name: 'profiles.show.reputation' })"
                                >
                                    <span class="text-xs font-medium leading-tight">{{ abbreviateNumber(resource.data.score) }}</span>
                                    <UserStar size="4" :score="resource.data.score" />
                                </button>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1 mt-2 text-white-foreground/80">
                            <div class="flex flex-row gap-1 items-center">
                                <IconUser class="size-4" />
                                <span class="text-sm">
                                    {{ resource.data.profile.type_label }}
                                </span>
                            </div>

                            <template v-if="resource.data.profile.type == 1 || resource.data.profile.type == 3">
                                <div class="flex flex-row gap-1 items-center">
                                    <IconViewStream class="size-4" />
                                    <span class="text-sm">
                                    {{ resource.data.profile.type_details.batch_year }} Batch {{ resource.data.profile.type_details.batch }} Stream {{ resource.data.profile.type_details.batch_stream }}
                                    </span>
                                </div>
                                <div class="flex flex-row gap-1 items-center">
                                    <IconLocationRadius class="size-4" />
                                    <span class="text-sm">
                                        {{ $store.state.app.location_options[resource.data.profile.type_details.batch_state] }}
                                    </span>
                                </div>
                            </template>

                            <div class="flex flex-row gap-1 items-center" v-if="resource.data.profile.school">
                                <IconSchool class="size-4" />
                                <span class="text-sm">
                                {{ resource.data.profile.school }}
                                </span>
                            </div>
                        </div>

                        <div class="mt-2" v-if="resource.data.profile.bio">
                            <LineClamp :lines="2">
                                <div class="text-sm" v-html="resource.data.profile.bio" />
                            </LineClamp>
                        </div>

                        <dl class="flex text-sm gap-4 mt-3">
                            <dt class="sr-only">Number of Posts</dt>
                            <dd class="flex gap-1">
                                <span class="font-medium">{{ resource.data.posts_count_abbrv }}</span>
                                <span class="opacity-70">Posts</span>
                            </dd>
                            <dd class="flex gap-1">
                                <span class="font-medium">{{ resource.data.questions_count_abbrv }}</span>
                                <span class="opacity-70">Questions</span>
                            </dd>
                        </dl>
                        <div v-if="!is_current_user" class="mt-4 flex gap-2 *:w-1/2 *:md:flex-grow-0">
                            <template v-if="!resource.data.blocked">
                                <template v-if="!connection">
                                    <AppButton :as="RouterLink" :to="{ name: 'profiles.show.connect' }" type="button" color="primary" compact capitalize>
                                        <IconUserAdd class="size-5" />
                                        <span>Connect</span>
                                    </AppButton>
                                </template>
                                <template v-else-if="!connection.accepted">
                                    <template v-if="resource.data.connection_sent_to_me">
                                        <AppButton
                                            :action="`/api/user-connections/${connection.id}`"
                                            action-method="delete"
                                            @action:success="() => {
                                                resource.data.connection_sent_to_me = null;
                                                $toast.success('Request Declined');
                                            }"
                                            color="danger-border"
                                            compact
                                            capitalize>
                                            <IconUserRemove class="size-5" />
                                            <span>Decline</span>
                                        </AppButton>
                                        <AppButton
                                            :action="`/api/user-connections/${connection.id}/accept`"
                                            action-method="post"
                                            @action:success="(data) => {
                                                resource.data.connection_sent_to_me = data.data;
                                                $toast.success('Connection Accepted');
                                            }"
                                            color="primary-border"
                                            compact
                                            capitalize>
                                            <IconUserCheck class="size-5" />
                                            <span>Accept</span>
                                        </AppButton>
                                    </template>
                                    <AppButton
                                        v-else
                                        :action="`/api/user-connections/${connection.id}`"
                                        action-method="delete"
                                        @action:success="() => {
                                            resource.data.connection_received_from_me = null;
                                            $toast.success('Request cancelled');
                                        }"
                                        color="gray-border"
                                        compact
                                        capitalize>
                                        <IconUserRemove class="size-5" />
                                        <span>Cancel Request</span>
                                    </AppButton>
                                </template>
                                <template v-else>
                                    <AppButton
                                        :as="RouterLink"
                                        :to="{ name: 'profiles.show.disconnect' }"
                                        color="gray-border"
                                        compact
                                        capitalize>
                                        <IconUserCheck class="size-5" />
                                        <span>Connected</span>
                                    </AppButton>
                                </template>


                                <AppButton @click="startChat(resource.data.id)" type="button" color="primary-border" compact capitalize>
                                    <IconMessageTextOutline class="size-5" />
                                    <span>Chat</span>
                                </AppButton>
                            </template>
                            <template v-else>
                                <div class="w-full max-md:grow">
                                    <AppButton
                                        @click="$store.blockUser({ user: resource.data, unblock: true }).then((result) => {
                                            if (!result.success) {
                                                return;
                                            }

                                            resource.data.blocked = false;
                                        })"
                                        type="button"
                                        color="danger-border"
                                        compact
                                        capitalize
                                        full>
                                        <span>Unblock</span>
                                    </AppButton>
                                </div>
                            </template>
                        </div>
                    </div>

                    <TabsRoot :model-value="state.activeTab" @update:model-value="(value) => state.activeTab = value">
                        <div class="pt-4 bg-white text-white-foreground">
                            <TabsList aria-label="Navigate Channel">
                                <TabsTrigger value="posts">
                                    Posts
                                </TabsTrigger>
                                <TabsTrigger value="questions">
                                    Questions
                                </TabsTrigger>
                            </TabsList>
                        </div>
                        <div class="py-3 md:px-4">
                            <TabsContent value="posts">
                                <KeepAlive>
                                    <PostList v-if="state.activeTab === 'posts' && resource.filled()" :collection="resourceRelations.posts" enable-tracking tracking-source="profile" />
                                </KeepAlive>
                            </TabsContent>
                            <TabsContent value="questions">
                                <KeepAlive>
                                    <PostList v-if="state.activeTab === 'questions' && resource.filled()" :collection="resourceRelations.questions" enable-tracking tracking-source="profile" />
                                </KeepAlive>
                            </TabsContent>
                        </div>
                    </TabsRoot>
                </template>

                <RouterView v-slot="{ Component }">
                    <component :is="Component" :resource="resource" />
                </RouterView>
            </content-body>
        </layout-content>
    </layout-main>
</template>
